import { qfRestGet } from './qfClient';

/**
 * `GET /v1/company-profile` — authoritative SEC company profile.
 *
 * The slow half of the Ticker Explorer card. QuantForge deliberately keeps the
 * company name OFF `/v1/symbol-summary` (`max-age=15`) and serves it here at
 * `max-age=3600`, because polling a static string every fifteen seconds is
 * waste. Two calls, two cadences.
 *
 * ⚠️ **Cookie-gated on QuantForge's side as of 2026-08-05, so this 401s our
 * REST bearer.** `/v1/symbol-summary` was moved to a bearer-admitting gate
 * (`_alpha_board`) in QF commit `a5e80042`; this router was not — it is still
 * mounted with the cookie-only `_alpha` and its handler takes a
 * `CurrentSession`. It works in a browser that carries a `qf_session` cookie
 * and holds Alpha, which is why the data is visible in the QF workspace but
 * not to us. The name renders blank until QF mounts this router the way
 * symbol-summary is mounted; nothing else on the card is affected.
 *
 * QF's response carries 20+ SEC fields (CIK, SIC, filings, HQ, former names).
 * Only what the card reads is typed here — adding a field is a one-line change
 * and an untyped field is invisible, which is the right trade for a shape we
 * do not own.
 */
export interface CompanyProfile {
  symbol: string;
  company_name: string | null;
  exchange: string | null;
  sector: string | null;
  industry: string | null;
  /** GICS where SEC's SIC-derived sector is too coarse. */
  gics_sector: string | null;
  gics_industry: string | null;
  /** Also on `/v1/symbol-summary`; identical source (UW `ticker_info`). */
  market_cap: number | null;
}

/**
 * One symbol's company profile.
 *
 * Reference data — resolved once per symbol change and held for the browser
 * cache's hour. Callers should tolerate a rejection rather than surfacing an
 * error: a missing company name is a blank, not a broken card.
 */
export async function fetchCompanyProfile(symbol: string): Promise<CompanyProfile> {
  return qfRestGet<CompanyProfile>('/v1/company-profile', {
    symbol: symbol.trim().toUpperCase(),
  });
}
